/**
 * Entry point for the application.
 */
// Restore the global setImmediate/clearImmediate that webpack provided via
// node-libs-browser (mobx-state-tree's scheduler and the remaining
// readable-stream polyfill consumers rely on it). Must run before the deps
// that use it — load it first.
import 'setimmediate';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
// Tailwind v4 entry — loaded before the legacy CSS so its cascade @layers sit
// below the (unlayered) SCSS, keeping the migration regression-free.
import './tailwind.css';
import 'reactsymbols-kit/ReactSymbolsKit.css';
// Global third-party DOM overrides (react-modal/select, reactsymbols spin).
import './styles/escape-hatch.css';
// Kept utility primitives used app-wide via literal class names.
import './styles/grid.css';
// Popover arrows (react-popover/FloatingPopover) — global; card/wrapper styled
// inline in FloatingPopover.tsx, only the CSS-triangle arrows kept here.
import './styles/popover.css';
// App-wide modal layer (.merlin-modal + per-modal blocks). ~10 modal components
// render these literal classes; earlier batches kept the hooks expecting this.
import './styles/modal.css';
import App from './App';
import registerServiceWorker from './registerServiceWorker';
import { Provider } from 'mobx-react';
import RootStore from './stores';
import makeInspectable from 'mobx-devtools-mst';
import api from './lib/api';
import * as authApi from './lib/auth';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { ErrorFallback } from './components/AppLayout/ErrorFallback';
import { type SnapshotIn } from 'mobx-state-tree';
import type { IRootStore } from './stores';
import type { IUI } from './stores/ui';

// Assigned by `createStore` before the first `renderApp` (and before any
// Bugsnag `onError` fires); definite-assignment keeps the legacy `let store;`
// hoisting byte-identical while giving the module-level handle a real type.
let store: IRootStore;

/**
 * Initialize the 'isTouch' property of the UI store.
 *
 * Adds an event listener to the document to listen for 'touchstart' and when
 * that event fires for the first time, set 'isTouch' to true and remove the
 * event listener again.
 *
 * @param ui the UI MobX State Tree store
 */
function initIsTouch(ui: IUI) {
  document.addEventListener('touchstart', function onFirstTouch() {
    ui.setIsTouch(true);
    document.removeEventListener('touchstart', onFirstTouch, false);
  });
}

/**
 * Create the MST store from a snapshot.
 *
 * Adds the api and authentication api to the environment of the store, so that
 * the stores can grab them with `getEnv`.
 *
 * @param snapshot the snapshot to initialize the store.
 */
function createStore(snapshot: SnapshotIn<typeof RootStore>): IRootStore {
  store = RootStore.create(snapshot, { api, authApi });
  makeInspectable(store);
  initIsTouch(store.ui);
  return store;
}

Bugsnag.start({
  apiKey: BUGSNAG_API_KEY,
  enabledReleaseStages: ['production', 'staging'],
  releaseStage: MERLIN_ENV,
  appVersion: VERSION,
  plugins: [new BugsnagPluginReact()],
  onError: function (event) {
    if (store.auth.user != null) {
      const user = store.auth.user;
      event.setUser(user.id, user.email, `${user.firstname} ${user.lastname}`);
    }
  },
});

// `getPlugin('react')` is typed `... | undefined`; the react plugin IS
// registered in `Bugsnag.start({ plugins: [new BugsnagPluginReact()] })` above,
// so the lookup never returns undefined here — assert non-null.
// The plugin's `BugsnagErrorBoundary` props type omits `children` (a stale
// pre-React-18 ComponentType shape); the boundary renders its children at
// runtime, so widen the component type to accept them at this single site.
const ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React) as React.ComponentType<
  React.PropsWithChildren<{
    FallbackComponent?: React.ComponentType<{
      error: Error;
      info: React.ErrorInfo;
      clearError: () => void;
    }>;
  }>
>;

// React 18: create the concurrent root once. renderApp() re-renders into it.
// No StrictMode — react-router v5 breaks under it and the dev double-render
// would diverge from byte-identical behavior.
// `getElementById` is typed `HTMLElement | null`; the `#root` mount node is
// guaranteed present in index.html. The original JS passed it unchecked —
// assert non-null to keep the call byte-identical.
const root = createRoot(document.getElementById('root')!);

// React Router v7 data router. Root-only: a single catch-all route renders the
// existing <App/>, which keeps its own declarative <Routes>. There are NO
// loaders/actions/data APIs here — the data router exists solely to unlock
// `useBlocker` (the v7 replacement for v5's <Prompt>), which is unavailable in
// plain <BrowserRouter> mode.
const router = createBrowserRouter([{ path: '*', element: <App /> }]);

/**
 * Render the application with the given store.
 *
 * Wraps the top-level app component in:
 * - Provider: the context for mobx-react's inject (must stay ABOVE the router so
 *   the injected store reaches App and every routed descendant)
 * - RouterProvider: the data-router context for react-router v7
 */
function renderApp(store: IRootStore) {
  if (process.env.NODE_ENV === 'development') {
    window.store = store;
  }
  root.render(
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Provider store={store}>
        <RouterProvider router={router} />
      </Provider>
    </ErrorBoundary>
  );
}

// Initial render of the application
renderApp(createStore({}));

// Register a service worker that caches code for faster loading. This is the
// standard service worker from Create React App:
// https://github.com/facebook/create-react-app/blob/master/packages/react-scripts/template/README.md#making-a-progressive-web-app
// It has been configured not to cache anything on /api and /auth, so that we
// can talk with the backend without interference of the service worker.
registerServiceWorker();

// Hot reloading: Vite full-reloads the app on source change. The previous
// store-preserving HMR was a webpack-HMR / react-hot-loader feature (dev-only,
// not application behavior), so a plain self-accept is byte-identical at runtime.
if (import.meta.hot) {
  import.meta.hot.accept();
}
